import axios from "axios";
import config from "../config";
const AuthService = require("../service/AuthService");

const APIUrl = config.API_BASE_URL;

// ============================================================================
// CACHING LAYER — in-memory L1 + localStorage L2
//
// Strategy:
//   - L1 (memory): checked first, zero parse cost, cleared on page reload
//   - L2 (localStorage): survives page reload, deserialized only on L1 miss
//   - stale-while-revalidate: if data is stale but not expired, serve it
//     immediately and revalidate in the background
//   - per-endpoint TTLs: frequently-changing data expires sooner
// ============================================================================

const TTL = {
  ACCOUNT:           2 * 60 * 1000,       // 2 min  — equity/BP changes after fills
  POSITIONS:         1 * 60 * 1000,       // 1 min  — positions change with orders
  ORDERS:            3 * 60 * 1000,       // 3 min  — orders list is large, slower-moving
  ACTIVITIES:        5 * 60 * 1000,       // 5 min  — fills are historical, stable
  PORTFOLIO_HISTORY:10 * 60 * 1000,       // 10 min — daily candles rarely change intraday
  ORDERS_DATE_TODAY: 3 * 60 * 1000,       // 3 min  — today's slice is live
  ORDERS_DATE_PAST:  24 * 60 * 60 * 1000, // 24 h   — past dates are immutable once market closes
};

// Prefix for per-date order cache keys — shared by TradeDashboard and DailyDetailDashboard
// Key format: orders_date_2026-03-04
const ORDERS_DATE_PREFIX = "orders_date_";

// Stale window: if age > TTL but < TTL * STALE_MULTIPLIER, serve stale + refresh in bg
const STALE_MULTIPLIER = 2;

const CACHE_KEYS = {
  ACCOUNT:          "alpaca_account",
  POSITIONS:        "alpaca_positions",
  ORDERS:           "alpaca_orders",
  ACTIVITIES:       "alpaca_activities",
  PORTFOLIO_HISTORY:"alpaca_portfolio_history",
};

// L1 in-memory store: { [key]: { data, timestamp, ttl } }
const memCache = {};

// Ongoing background revalidation promises (prevent duplicate requests)
const inflight = {};

// -------------------------------------------------------------------------
// Core cache helpers
// -------------------------------------------------------------------------

const readL2 = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw); // { data, timestamp, ttl }
  } catch {
    return null;
  }
};

const writeL2 = (key, entry) => {
  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // quota exceeded — silently skip L2
  }
};

const removeL2 = (key) => {
  try { localStorage.removeItem(key); } catch {}
};

/** Write both layers */
const setCache = (key, data, ttl) => {
  const entry = { data, timestamp: Date.now(), ttl };
  memCache[key] = entry;
  writeL2(key, entry);
};

/**
 * Read cache.
 * Returns { data, status } where status is:
 *   "fresh"  — within TTL, use directly
 *   "stale"  — past TTL but within stale window, use + trigger background refresh
 *   "miss"   — expired or not found
 */
const getCache = (key, ttl) => {
  // L1 first
  let entry = memCache[key];

  // L2 fallback
  if (!entry) {
    entry = readL2(key);
    if (entry) memCache[key] = entry; // promote to L1
  }

  if (!entry) return { data: null, status: "miss" };

  const age = Date.now() - entry.timestamp;
  const effectiveTTL = ttl ?? entry.ttl ?? 5 * 60 * 1000;

  if (age <= effectiveTTL) {
    console.log(`[Cache L1/L2] HIT  ${key} (${Math.round(age / 1000)}s old, fresh)`);
    return { data: entry.data, status: "fresh" };
  }

  if (age <= effectiveTTL * STALE_MULTIPLIER) {
    console.log(`[Cache L1/L2] STALE ${key} (${Math.round(age / 1000)}s old, serving + revalidating)`);
    return { data: entry.data, status: "stale" };
  }

  // Expired — clean up
  delete memCache[key];
  removeL2(key);
  console.log(`[Cache L1/L2] MISS  ${key} (${Math.round(age / 1000)}s old, expired)`);
  return { data: null, status: "miss" };
};

// -------------------------------------------------------------------------
// Public cache utilities
// -------------------------------------------------------------------------

/** Returns age in seconds or null if no cache entry */
export const getCacheAge = (key) => {
  const entry = memCache[key] ?? readL2(key);
  if (!entry) return null;
  return Math.round((Date.now() - entry.timestamp) / 1000);
};

/**
 * Returns full cache status for UI display:
 * { key, age, status, ttl, expiresIn }
 */
export const getCacheStatus = (key) => {
  const ttl = TTL[Object.keys(CACHE_KEYS).find(k => CACHE_KEYS[k] === key)] ?? 5 * 60 * 1000;
  const entry = memCache[key] ?? readL2(key);
  if (!entry) return { key, age: null, status: "miss", ttl, expiresIn: null };

  const age = Date.now() - entry.timestamp;
  const expiresIn = Math.max(0, ttl - age);
  const status = age <= ttl ? "fresh" : age <= ttl * STALE_MULTIPLIER ? "stale" : "miss";

  return { key, age: Math.round(age / 1000), status, ttl, expiresIn: Math.round(expiresIn / 1000) };
};

/** Invalidate one or more cache keys (pass key string or array) */
export const invalidateCache = (keys) => {
  const list = Array.isArray(keys) ? keys : [keys];
  list.forEach(k => {
    delete memCache[k];
    removeL2(k);
    console.log(`[Cache] Invalidated: ${k}`);
  });
};

/** Clear every Alpaca cache */
export const clearAllCaches = () => {
  Object.values(CACHE_KEYS).forEach(k => {
    delete memCache[k];
    removeL2(k);
    // also clear portfolio-history variants and orders_date_ per-date keys
    Object.keys(localStorage).filter(lk => lk.startsWith(k)).forEach(lk => {
      localStorage.removeItem(lk);
    });
  });
  // clear per-date order slices
  Object.keys(localStorage).filter(k => k.startsWith(ORDERS_DATE_PREFIX)).forEach(k => {
    localStorage.removeItem(k);
    delete memCache[k];
  });
  console.log("[Cache] All Alpaca caches cleared");
};

// -------------------------------------------------------------------------
// Auth helper
// -------------------------------------------------------------------------
const getAuthHeaders = () => ({
  "x-api-key": config.API_KEY,
  authorizationToken: AuthService.getToken(),
});

const getUsername = () => sessionStorage.getItem("user");

const assertAuth = () => {
  const username = getUsername();
  const token = AuthService.getToken();
  if (!username || !token) throw new Error("No authentication credentials found");
  return { username, token };
};

const handleAuthError = (error) => {
  if (error.response && (error.response.status === 401 || error.response.status === 403)) {
    console.warn("[Auth] Token expired — redirecting to login");
    AuthService.resetUserSession();
    window.location.href = "/Login";
  }
};

// -------------------------------------------------------------------------
// Generic fetcher with stale-while-revalidate
// -------------------------------------------------------------------------
const fetchWithCache = async ({
  cacheKey,
  ttl,
  forceRefresh,
  fetcher,        // async () => data
}) => {
  // Force refresh: skip cache entirely
  if (forceRefresh) {
    const data = await fetcher();
    setCache(cacheKey, data, ttl);
    return data;
  }

  const { data: cached, status } = getCache(cacheKey, ttl);

  if (status === "fresh") return cached;

  if (status === "stale") {
    // Serve stale immediately; kick off background refresh if not already inflight
    if (!inflight[cacheKey]) {
      inflight[cacheKey] = fetcher()
        .then(fresh => {
          setCache(cacheKey, fresh, ttl);
          console.log(`[Cache] Background refresh complete: ${cacheKey}`);
        })
        .catch(err => console.warn(`[Cache] Background refresh failed: ${cacheKey}`, err))
        .finally(() => { delete inflight[cacheKey]; });
    }
    return cached;
  }

  // miss — if another call is already inflight, wait for it
  if (inflight[cacheKey]) {
    console.log(`[Cache] Waiting for inflight request: ${cacheKey}`);
    await inflight[cacheKey].catch(() => {});
    const { data: fresh } = getCache(cacheKey, ttl);
    if (fresh) return fresh;
  }

  // full fetch
  const promise = fetcher().then(data => {
    setCache(cacheKey, data, ttl);
    return data;
  });

  inflight[cacheKey] = promise.finally(() => { delete inflight[cacheKey]; });
  return promise;
};

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Fetches Alpaca account information
 * @param {boolean} forceRefresh
 */
export const fetchAlpacaAccount = async (forceRefresh = false) => {
  const { username } = assertAuth();
  return fetchWithCache({
    cacheKey: CACHE_KEYS.ACCOUNT,
    ttl: TTL.ACCOUNT,
    forceRefresh,
    fetcher: async () => {
      try {
        const res = await axios.post(
          `${APIUrl}/alpaca/account`,
          { user: { username } },
          { headers: getAuthHeaders() }
        );
        console.log("[Alpaca] Account fetched:", res.data);
        return res.data;
      } catch (err) {
        handleAuthError(err);
        throw err;
      }
    },
  });
};

/**
 * Fetches Alpaca positions
 * @param {boolean} forceRefresh
 */
export const fetchAlpacaPositions = async (forceRefresh = false) => {
  const { username } = assertAuth();
  return fetchWithCache({
    cacheKey: CACHE_KEYS.POSITIONS,
    ttl: TTL.POSITIONS,
    forceRefresh,
    fetcher: async () => {
      try {
        const res = await axios.post(
          `${APIUrl}/alpaca/positions`,
          { user: { username } },
          { headers: getAuthHeaders() }
        );
        console.log("[Alpaca] Positions fetched:", res.data?.length, "items");
        return res.data;
      } catch (err) {
        handleAuthError(err);
        throw err;
      }
    },
  });
};

// ============================================================================
// DATE HELPERS
// ============================================================================

/**
 * Returns "YYYY-MM-DD" for a Date using LOCAL calendar date (not UTC).
 * Never use toISOString().slice(0,10) — that gives the UTC date which in ET
 * is one day ahead after 8pm, causing cache key mismatches.
 */
const toLocalDateStr = (d) => {
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// ============================================================================
// DATE RANGE HELPERS
// ============================================================================

/**
 * Returns the Monday–Friday ISO date range for the current trading week.
 *
 * "Current week" means the Mon–Fri block that contains today.
 * On a weekend, this returns the week that just ended (previous Fri).
 *
 * Returns { after, until } as RFC 3339 strings suitable for Alpaca's API.
 *   after  = Monday 00:00:00 ET expressed as UTC ISO string
 *   until  = Friday 23:59:59 ET expressed as UTC ISO string (or today if mid-week)
 *
 * ET offset is approximated at UTC-4 (EDT) / UTC-5 (EST).
 * For paper trading this precision is fine — we just need Mon–Fri boundaries.
 */
export const getWeekDateRange = () => {
  const now = new Date();

  // Start = 10 days ago at local midnight
  const start = new Date(now);
  start.setDate(now.getDate() - 10);
  start.setHours(0, 0, 0, 0);

  // End = today (no future)
  const end = new Date(now);

  console.log(
    `[Orders] Rolling 10-day range: ${toLocalDateStr(start)} → ${toLocalDateStr(end)}`
  );

  return {
    after: toLocalDateStr(start),
    until: toLocalDateStr(end),
  };
};

/**
 * Fetches Alpaca orders for the current trading week by default.
 *
 * Callers can override any parameter via the filters argument:
 *   fetchAlpacaOrders({ after: '...', until: '...', limit: 2000 })
 *
 * The cache key includes the week's Monday date so that a new week
 * automatically produces a cache miss rather than serving last week's data.
 *
 * @param {object}  filters       — override defaults: { after, until, limit, status, direction }
 * @param {boolean} forceRefresh  — bypass cache
 */
export const fetchAlpacaOrders = async (filters = {}, forceRefresh = false) => {
  const { username } = assertAuth();

  // Build week range — caller may override after/until/limit
  const weekRange = getWeekDateRange();
  const resolved  = {
    status:    "all",
    limit:     1500,          // safe ceiling for 1 week of 0DTE bracket orders
    direction: "desc",
    after:     weekRange.after,
    nested:true,
    //until:     weekRange.until,
    ...filters,               // caller overrides win
  };

  // Include Monday's date in the cache key so new weeks get a fresh fetch
  const weekKey = weekRange.after.slice(0, 10); // "YYYY-MM-DD"
  const cacheKey = `${CACHE_KEYS.ORDERS}_${weekKey}`;

  return fetchWithCache({
    cacheKey,
    ttl: TTL.ORDERS,
    forceRefresh,
    fetcher: async () => {
      try {
        const res = await axios.post(
          `${APIUrl}/alpaca/orders`,
          { user: { username }, ...resolved },
          { headers: getAuthHeaders() }
        );
        console.log(
          `[Alpaca] Orders fetched: ${res.data?.length} items`,
          `(${resolved.after?.slice(0,10)} → ${resolved.until?.slice(0,10)})`
        );
        return res.data;
      } catch (err) {
        handleAuthError(err);
        throw err;
      }
    },
  });
};

// ============================================================================
// PER-DATE ORDER CACHE — shared by TradeDashboard and DailyDetailDashboard
//
// Cache key format:  orders_date_2026-03-04
// TTL:               24h for past dates (immutable), 3min for today (live)
//
// Sweep:  on every multi-day fetch, any orders_date_* key whose date falls
//         outside the current 10-day window is evicted from both L1 and L2.
//         This keeps localStorage permanently bounded to ~10-14 keys max.
// ============================================================================

/**
 * Evict any orders_date_YYYY-MM-DD keys older than windowStartDate.
 * Called automatically by fetchMultiDayOrders on every load.
 * @param {string} windowStartDate — "YYYY-MM-DD" oldest date to keep
 */
const sweepOldDateKeys = (windowStartDate) => {
  const cutoff = new Date(windowStartDate).getTime();

  // Sweep L2 (localStorage)
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith(ORDERS_DATE_PREFIX))
      .forEach(k => {
        const dateStr = k.slice(ORDERS_DATE_PREFIX.length); // "2026-03-04"
        if (new Date(dateStr).getTime() < cutoff) {
          localStorage.removeItem(k);
          delete memCache[k];
          console.log(`[Cache] Swept stale date key: ${k}`);
        }
      });
  } catch {
    // localStorage unavailable — L1 sweep continues below
  }

  // Sweep L1 (memCache) for keys that may not be in localStorage
  Object.keys(memCache)
    .filter(k => k.startsWith(ORDERS_DATE_PREFIX))
    .forEach(k => {
      const dateStr = k.slice(ORDERS_DATE_PREFIX.length);
      if (new Date(dateStr).getTime() < cutoff) {
        delete memCache[k];
        console.log(`[Cache] Swept stale L1 date key: ${k}`);
      }
    });
};

/**
 * Write a single date's order slice to the per-date cache.
 * Uses 24h TTL for past dates, 3min TTL for today.
 * @param {string} dateStr — "YYYY-MM-DD"
 * @param {Array}  data    — flat order array for that date
 */
const cacheDateSlice = (dateStr, data) => {
  const todayStr = toLocalDateStr(new Date());
  const ttl = dateStr === todayStr ? TTL.ORDERS_DATE_TODAY : TTL.ORDERS_DATE_PAST;
  const key = `${ORDERS_DATE_PREFIX}${dateStr}`;
  setCache(key, data, ttl);
  console.log(`[Cache] Wrote ${key} — ${data.length} orders, TTL ${Math.round(ttl / 60000)}min`);
};

/**
 * Fetches all orders for the rolling 10-day window from the BerBul /orders endpoint.
 *
 * Response shape: { days: [ { dt: "YYYY-MM-DD", data: [...] } ], totalCount, lastEvaluatedKey }
 *
 * After fetching:
 *   - Sweeps stale per-date cache keys outside the window (keeps localStorage bounded)
 *   - Writes each dt slice to its own cache key (orders_date_YYYY-MM-DD)
 *   - Returns a flat merged array for TradeDashboard's buildLedgerData pipeline
 *
 * Fast path: if today's slice is fresh in cache, assembles the full flat array
 * from per-date cache keys without hitting the network at all.
 *
 * @param {boolean} forceRefresh — bypass cache for all slices
 */
/**
 * Returns the cached order slice for a single date, fetching only on a miss.
 *
 * Used by DailyDetailDashboard instead of getDataAPI(dateParam, "orders").
 * When the user navigates from TradeDashboard the cache is already warm —
 * this call costs zero network. On a cold/direct URL load it fetches just
 * that one date with after: dateStr and no days cap.
 *
 * @param {string}  dateStr      — "YYYY-MM-DD"
 * @param {boolean} forceRefresh — bypass cache
 * @returns {Promise<Array>} flat order array for that date
 */
export const fetchOrdersByDate = async (dateStr, forceRefresh = false) => {
  const todayStr = toLocalDateStr(new Date());
  const ttl = dateStr === todayStr ? TTL.ORDERS_DATE_TODAY : TTL.ORDERS_DATE_PAST;
  const cacheKey = `${ORDERS_DATE_PREFIX}${dateStr}`;

  if (!forceRefresh) {
    const { data, status } = getCache(cacheKey, ttl);
    if (status !== "miss" && data) {
      console.log(`[Orders] Cache hit for ${dateStr} — ${data.length} orders`);
      return data;
    }
  }

  // Cache miss — fetch this specific date
  // Backend returns { days: [{ dt, data }] } — find the matching dt slice
  console.log(`[Orders] Cache miss for ${dateStr} — fetching from /orders`);
  try {
    const res = await axios.post(
      `${APIUrl}/orders`,
      { startDate: dateStr, username: "admin" },
      { headers: { "x-api-key": config.API_KEY, authorizationToken: AuthService.getToken() } }
    );

    const { days = [] } = res.data;
    console.log(`[Orders] API returned ${days.length} day(s) for ${dateStr}:`, days.map(d => d.dt));

    // Backend always returns { days: [{ dt, data }] } — match by dt
    const match = days.find(d => d.dt === dateStr);
    const data = match?.data ?? days[0]?.data ?? [];  // fallback to first day if dt differs

    cacheDateSlice(dateStr, data);
    console.log(`[Orders] Cached ${data.length} orders for ${dateStr}`);
    return data;
  } catch (err) {
    handleAuthError(err);
    throw err;
  }
};

export const fetchMultiDayOrders = async (forceRefresh = false) => {
  const now = new Date();
  const todayStr     = toLocalDateStr(now);
  const windowStart  = new Date(now);
  windowStart.setDate(now.getDate() - 9);          // today − 9 = 10 calendar days inclusive
  const windowStartStr = toLocalDateStr(windowStart);

  sweepOldDateKeys(windowStartStr);

  // Cache fast-path — ONLY use if every date returned by the last API call is
  // still present and fresh. A partial cache (some dates missing) means we had
  // fewer days on a prior fetch and must re-fetch to get the full window.
  if (!forceRefresh) {
    // Collect all fresh per-date slices within the window
    const cachedDates = [];
    const flat = [];
    let todayFresh = false;

    Object.keys(localStorage)
      .filter(k => k.startsWith(ORDERS_DATE_PREFIX))
      .forEach(k => {
        const dateStr = k.slice(ORDERS_DATE_PREFIX.length);
        if (new Date(dateStr).getTime() < new Date(windowStartStr).getTime()) return;
        const ttl = dateStr === todayStr ? TTL.ORDERS_DATE_TODAY : TTL.ORDERS_DATE_PAST;
        const { data, status } = getCache(k, ttl);
        if (status !== "miss" && data) {
          cachedDates.push(dateStr);
          flat.push(...data);
          if (dateStr === todayStr) todayFresh = true;
        }
      });

    // Only trust the cache if today is fresh AND we have the same number of
    // dates the last API call returned (stored in a sentinel key).
    const sentinelKey = "orders_day_count";
    const sentinelRaw = localStorage.getItem(sentinelKey);
    const expectedDayCount = sentinelRaw ? parseInt(sentinelRaw, 10) : 0;

    if (todayFresh && expectedDayCount > 0 && cachedDates.length >= expectedDayCount) {
      console.log(`[Orders] Full cache hit — ${flat.length} orders from ${cachedDates.length} date slices`);
      return flat;
    }

    if (!todayFresh) {
      console.log(`[Orders] Today not fresh — fetching`);
    } else {
      console.log(`[Orders] Partial cache (${cachedDates.length}/${expectedDayCount} dates) — fetching`);
    }
  }

  // Single API call — backend returns { days: [{ dt, data }] } for the full window
  console.log(`[Orders] Fetching window ${windowStartStr} → ${todayStr}`);
  try {
    const res = await axios.post(
      `${APIUrl}/orders`,
      { startDate: windowStartStr, username: "admin" },
      { headers: { "x-api-key": config.API_KEY, authorizationToken: AuthService.getToken() } }
    );

    const { days = [] } = res.data;
    console.log(`[Orders] API returned ${days.length} days:`, days.map(d => d.dt));

    // Persist the expected day count so future fast-path checks are accurate
    try { localStorage.setItem("orders_day_count", String(days.length)); } catch {}

    // Write each day to its own cache slot — fetchOrdersByDate will hit cache on next nav
    days.forEach(({ dt, data }) => {
      if (dt && Array.isArray(data)) cacheDateSlice(dt, data);
    });

    return days.flatMap(d => d.data || []);
  } catch (err) {
    handleAuthError(err);
    throw err;
  }
};


/**
 * Fetches Alpaca activities (fills, fees, dividends)
 * @param {boolean} forceRefresh
 */
export const fetchAlpacaActivities = async (forceRefresh = false) => {
  const { username } = assertAuth();
  return fetchWithCache({
    cacheKey: CACHE_KEYS.ACTIVITIES,
    ttl: TTL.ACTIVITIES,
    forceRefresh,
    fetcher: async () => {
      try {
        const res = await axios.post(
          `${APIUrl}/alpaca/activities`,
          { user: { username } },
          { headers: getAuthHeaders() }
        );
        console.log("[Alpaca] Activities fetched:", res.data?.length, "items");
        return res.data;
      } catch (err) {
        handleAuthError(err);
        throw err;
      }
    },
  });
};

/**
 * Fetches Alpaca portfolio history (equity curve)
 * @param {string} period   — '1W' | '1M' | '3M' | '6M' | '1A' | 'all'
 * @param {string} timeframe — '1Min' | '5Min' | '15Min' | '1H' | '1D' | '1W'
 * @param {boolean} forceRefresh
 */
export const fetchAlpacaPortfolioHistory = async (
  period = "1M",
  timeframe = "1D",
  forceRefresh = false
) => {
  const { username } = assertAuth();
  // Include period+timeframe in cache key so different views don't collide
  const cacheKey = `${CACHE_KEYS.PORTFOLIO_HISTORY}_${period}_${timeframe}`;

  return fetchWithCache({
    cacheKey,
    ttl: TTL.PORTFOLIO_HISTORY,
    forceRefresh,
    fetcher: async () => {
      try {
        const res = await axios.post(
          `${APIUrl}/alpaca/portfolio-history`,
          { user: { username }, period, timeframe },
          { headers: getAuthHeaders() }
        );
        console.log(`[Alpaca] Portfolio history fetched (${period}/${timeframe}):`, res.data);
        return res.data;
      } catch (err) {
        handleAuthError(err);
        throw err;
      }
    },
  });
};

/**
 * Prefetch all core endpoints in parallel.
 * Call this on app init or after login so data is warm before the user navigates.
 */
export const prefetchAll = async () => {
  console.log("[Cache] Prefetching all Alpaca endpoints...");
  await Promise.allSettled([
    fetchAlpacaAccount(),
    fetchAlpacaPositions(),
    fetchAlpacaOrders(),
    fetchAlpacaActivities(),
    fetchAlpacaPortfolioHistory(),
  ]);
  console.log("[Cache] Prefetch complete");
};